import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Download, Package, ArrowLeft, Sparkles, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function MyTools() {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [confirming, setConfirming] = useState(!!sessionId);
  const { user } = useAuth();

  useEffect(() => {
    if (sessionId) {
      // Poll checkout status until paid (or timeout ~30s)
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          // We need tool_id to hit the status endpoint — grab it from the latest payment_transactions by scanning /tools/mine
          // Simpler: just refresh /tools/mine and check if any new tool appears
          const { data } = await api.get('/tools/mine');
          setTools(data);
          if (data.length > 0 || attempts >= 15) {
            setConfirming(false);
            setLoading(false);
            if (data.length > 0) toast.success('Your tool is ready to download 🎉');
            return;
          }
        } catch (_) { /* keep polling */ }
        setTimeout(poll, 2000);
      };
      poll();
    } else {
      load();
    }
  }, [sessionId]);

  const load = async () => {
    try {
      const { data } = await api.get('/tools/mine');
      setTools(data);
    } catch (_) {
      toast.error('Could not load your toolkit');
    } finally {
      setLoading(false);
    }
  };

  const download = async (tool) => {
    try {
      // Signed URL → native browser download. Preserves original filename +
      // works reliably on iPad Safari (blob-based downloads can be blocked).
      const { data } = await api.post(`/tools/${tool.id}/download-url`);
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not download');
    }
  };

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />
      <div className="max-w-5xl mx-auto px-6 py-12">
        <Link to="/tools" className="inline-flex items-center gap-2 text-sm mb-6" style={{ fontFamily: 'Nunito, sans-serif', color: '#E57A44', fontWeight: 600 }} data-testid="mytools-back">
          <ArrowLeft className="w-4 h-4" /> Back to Sacred Tools
        </Link>

        <div className="mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>My Toolkit</h1>
          <p style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Everything you've unlocked, ready whenever you need it.</p>
        </div>

        {confirming && (
          <div className="p-6 rounded-3xl mb-8 flex items-center gap-4" style={{ background: 'linear-gradient(135deg, #FFF5E5 0%, #FFFBF3 100%)', border: '1px solid #E8C26540' }} data-testid="mytools-confirming">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#E57A44' }} />
            <p style={{ fontFamily: 'Nunito, sans-serif', color: '#4A3D1E' }}>Confirming your purchase with Stripe… this takes a few seconds.</p>
          </div>
        )}

        {loading ? (
          <div className="text-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto" style={{ borderColor: '#E57A44' }} /></div>
        ) : tools.length === 0 ? (
          <div className="p-14 rounded-3xl text-center max-w-xl mx-auto" style={{ backgroundColor: '#FFFFFF', border: '1px dashed #E6E2D8' }} data-testid="mytools-empty">
            <Package className="w-12 h-12 mx-auto mb-4" style={{ color: '#E8C265' }} />
            <p className="text-lg mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29', fontWeight: 600 }}>Your toolkit is empty</p>
            <p className="mb-5" style={{ fontFamily: 'Lora, serif', color: '#595553', fontStyle: 'italic' }}>Browse the shop and grab a tool that meets you where you are.</p>
            <Link to="/tools" className="inline-flex items-center gap-2 px-6 py-3 rounded-full hover:-translate-y-0.5 transition"
              style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FFFFFF', fontWeight: 700 }}>
              <Sparkles className="w-4 h-4" /> Browse Sacred Tools
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {tools.map(t => (
              <div key={t.id} className="rounded-3xl overflow-hidden" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0,0,0,0.04)', border: '1px solid #F0EBE0' }} data-testid={`mytools-item-${t.id}`}>
                <div className="h-40" style={{ background: t.cover_url ? `url(${t.cover_url}) center/cover` : 'linear-gradient(135deg, #78A18E30 0%, #78A18E10 100%)' }}>
                  {!t.cover_url && <div className="h-full flex items-center justify-center"><Package className="w-12 h-12" style={{ color: '#78A18E', opacity: 0.5 }} /></div>}
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-1 mb-2 text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#78A18E', fontWeight: 700 }}>
                    <CheckCircle className="w-3.5 h-3.5" /> {t.admin_access ? 'Admin access' : `Owned · ${t.purchased_at ? new Date(t.purchased_at).toLocaleDateString() : ''}`}
                  </div>
                  <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29', lineHeight: 1.3 }}>{t.title}</h3>
                  <button onClick={() => download(t)} className="w-full py-2.5 rounded-full flex items-center justify-center gap-2 hover:-translate-y-0.5 transition"
                    style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#78A18E', color: '#FFFFFF', fontWeight: 700, fontSize: '14px' }}
                    data-testid={`mytools-download-${t.id}`}>
                    <Download className="w-4 h-4" /> Download
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
